import { useState, useEffect } from 'react';
import { 
  QrCode, 
  Download, 
  Smartphone, 
  CheckCircle2, 
  Copy, 
  ExternalLink,
  Trash2,
  Plus,
  Loader2,
  Lock,
  Link2,
  X,
  Sparkles
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { downloadStazionePdf, fetchStazioneStatus } from '../lib/stazionePdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal Marketing per Stazione Digitale
 */
const StazioneMarketingModal = ({ isOpen, onClose, onPurchase, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <QrCode className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Stazione Digitale</h3>
              <p className="text-purple-200 text-sm">Il QR Code universale</p>
            </div>
          </div>
        </div>

        {/* Contenuto */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            <strong>Porta CuraCheck in tutta la casa.</strong> Scarica e stampa la tua Stazione Digitale professionale: 
            un QR Code unico da applicare dove vuoi per facilitare il lavoro dell'assistente.
          </p>

          {/* Vantaggi */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Accesso Istantaneo</p>
                <p className="text-sm text-gray-500">L'assistente scansiona e accede subito, senza link o password</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Fino a 4 Dispositivi</p>
                <p className="text-sm text-gray-500">Più assistenti possono usare lo stesso QR</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">PDF Professionale</p>
                <p className="text-sm text-gray-500">Stampa e applica in cucina, camera, o dove preferisci</p>
              </div>
            </div>
          </div>

          {/* Prezzo e CTA */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Una tantum</p>
                <p className="text-3xl font-bold text-purple-700">€3,90</p>
              </div>
              <Button
                onClick={onPurchase}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg"
                data-testid="btn-purchase-stazione"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Acquista Ora
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente Stazione Digitale per la Dashboard Famiglia
 * Mostra:
 * 1. Link di Accesso - SOLO se ci sono medicine configurate
 * 2. Scarica PDF - sbloccato se acquistata (€3,90 una tantum) o inclusa nel piano Premium
 */
const StazioneDigitaleCard = ({ famigliaId, hasStazione, familyCode, onPurchase, familyToken, medicines = [], onNavigateToMedicines, isPremium = false }) => {
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [localFamilyCode, setLocalFamilyCode] = useState(familyCode);
  const [caregiverUrl, setCaregiverUrl] = useState(null);
  // Stazione Digitale: inclusa nel piano Premium, altrimenti acquisto una tantum (€3,90)
  const [localHasStazione, setLocalHasStazione] = useState(hasStazione || isPremium);
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Controlla se ci sono medicine configurate
  const hasMedicines = medicines && medicines.length > 0;

  // Genera il link di accesso per l'assistente
  const accessLink = familyToken ? `${window.location.origin}/app/${familyToken}` : '';

  // Carica dispositivi se ha la stazione
  useEffect(() => {
    if (localHasStazione && famigliaId) {
      loadDevices();
    }
  }, [localHasStazione, famigliaId]);

  // Link assistente del QR (stesso del PDF); per Premium senza codice, lo stato lo crea al primo utilizzo
  useEffect(() => {
    if (localHasStazione && famigliaId) {
      fetchStazioneStatus(famigliaId)
        .then((data) => {
          if (data.caregiver_url) setCaregiverUrl(data.caregiver_url);
          if (data.family_code) setLocalFamilyCode((code) => code || data.family_code);
        })
        .catch((err) => console.error('Stazione status error:', err));
    }
  }, [localHasStazione, famigliaId]);

  // Sync props con stato locale
  useEffect(() => {
    // Stazione attiva se acquistata o inclusa nel piano Premium
    setLocalHasStazione(hasStazione || isPremium);
    setLocalFamilyCode(familyCode);
  }, [hasStazione, familyCode, isPremium]);

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stazione/devices/${famigliaId}`);
      const data = await response.json();
      setDevices(data.devices || []);
    } catch (err) {
      console.error('Load devices error:', err);
    } finally {
      setDevicesLoading(false);
    }
  };

  // Copia link di accesso
  const handleCopyAccessLink = () => {
    if (!accessLink) {
      toast.error('Link non disponibile');
      return;
    }
    navigator.clipboard.writeText(accessLink);
    setLinkCopied(true);
    toast.success('Link copiato! Invialo all\'assistente');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!confirm('Rimuovere questo dispositivo?')) return;
    
    try {
      await fetch(`${API_URL}/api/stazione/devices/${deviceId}`, {
        method: 'DELETE'
      });
      toast.success('Dispositivo rimosso');
      loadDevices();
    } catch (err) {
      toast.error('Errore durante la rimozione');
    }
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      // Crea sessione Stripe per acquisto Stazione Digitale
      const response = await fetch(`${API_URL}/api/checkout/stazione-digitale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          famiglia_id: famigliaId,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirect a Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Errore durante la creazione del checkout');
      }
    } catch (err) {
      console.error('Stazione checkout error:', err);
      toast.error(err.message || 'Errore durante l\'acquisto');
    } finally {
      setLoading(false);
      setShowMarketingModal(false);
    }
  };

  // Genera e scarica il PDF della Stazione Digitale
  const handleDownloadPDF = async () => {
    if (!localHasStazione) {
      // Mostra modal marketing se non ha acquistato
      setShowMarketingModal(true);
      return;
    }

    setPdfLoading(true);
    try {
      await downloadStazionePdf(famigliaId, localFamilyCode);
      toast.success('PDF scaricato! Stampalo e appendilo in casa');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error(err.message || 'Errore nella generazione del PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Rendering principale - mostra sempre entrambi i pulsanti
  return (
    <>
      {/* Modal Marketing */}
      <StazioneMarketingModal
        isOpen={showMarketingModal}
        onClose={() => setShowMarketingModal(false)}
        onPurchase={handlePurchase}
        loading={loading}
      />

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4A7C59] to-[#5DADE2] p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Link2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold">Accesso Assistente Familiare</h3>
              <p className="text-sm text-white/80">
                {localHasStazione 
                  ? 'Condividi il link o stampa il QR Code'
                  : 'Condividi il link con l\'assistente'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* SEZIONE 1: Link di Accesso - SOLO SE CI SONO MEDICINE */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-[#4A7C59]" />
              <h4 className="font-semibold text-gray-800">Link di Accesso</h4>
              {hasMedicines ? (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Pronto</span>
              ) : (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Configura medicine</span>
              )}
            </div>
            
            {hasMedicines ? (
              <>
                <p className="text-sm text-gray-600">
                  Copia questo link e invialo all'assistente. Aprirà l'app con accesso automatico.
                </p>

                {/* Link copiabile */}
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 p-2">
                  <p className="flex-1 px-2 font-mono text-xs text-gray-600 truncate">
                    {accessLink || 'Link non disponibile'}
                  </p>
                  <Button
                    onClick={handleCopyAccessLink}
                    disabled={!accessLink}
                    className={`rounded-lg px-4 ${linkCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-[#4A7C59] hover:bg-[#3A6345]'}`}
                    data-testid="btn-copy-access-link"
                  >
                    {linkCopied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Copiato!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copia Link
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* Avviso: configura prima le medicine */
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800 mb-3">
                  <strong>Prima configura le medicine!</strong> L'assistente ha bisogno di sapere quali farmaci gestire.
                </p>
                <Button
                  onClick={onNavigateToMedicines}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                >
                  Configura Medicine →
                </Button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* SEZIONE 2: Scarica PDF Stazione Digitale (FREEMIUM) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <QrCode className="w-4 h-4 text-purple-600" />
              <h4 className="font-semibold text-gray-800">Stazione Digitale</h4>
              {localHasStazione ? (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Sbloccato</span>
              ) : (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Premium
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600">
              {localHasStazione 
                ? 'Scarica il PDF con il QR Code da stampare e appendere in casa.'
                : 'Stampa un QR Code professionale da applicare in casa. L\'assistente lo scansiona e accede subito.'}
            </p>

            {/* QR Code assistente (stesso link stampato nel PDF) */}
            {localHasStazione && caregiverUrl && (
              <div className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border border-purple-200" data-testid="stazione-caregiver-qr">
                <QRCodeSVG value={caregiverUrl} size={160} level="M" includeMargin />
                <p className="text-xs text-gray-500 text-center">Inquadra con la fotocamera per aprire l'app assistente</p>
              </div>
            )}

            {/* Pulsante Scarica PDF / Sblocca */}
            {localHasStazione ? (
              <Button
                onClick={handleDownloadPDF}
                disabled={pdfLoading}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg px-4 py-3 font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                data-testid="btn-download-pdf-stazione"
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Scarica PDF con QR Code
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleDownloadPDF}
                disabled={pdfLoading}
                className="w-full rounded-xl py-3 font-semibold transition-all bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                data-testid="btn-download-pdf-stazione"
              >
                {pdfLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Sblocca Stazione Digitale - €3,90
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Dispositivi Associati (solo se ha la stazione) */}
          {localHasStazione && (
            <>
              <div className="border-t border-gray-200" />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Dispositivi Associati ({devices.length}/4)
                  </h4>
                  {devicesLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </div>

                {devices.length > 0 ? (
                  <div className="space-y-2">
                    {devices.map(device => (
                      <div 
                        key={device.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Smartphone className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-sm text-gray-700">{device.device_name}</p>
                            <p className="text-xs text-gray-500">
                              {device.browser} • {device.push_enabled ? '🔔 Push attive' : '🔕 Push disattive'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveDevice(device.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Rimuovi dispositivo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">Nessun dispositivo ancora associato</p>
                    <p className="text-xs text-gray-400">Appariranno qui quando l'assistente scansiona il QR</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Link Video Tutorial */}
          <div className="border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-500 mb-2 text-center">Video tutorial per l'assistente</p>
            <div className="flex gap-2">
              <a 
                href="https://youtu.be/wkfKclETFk4" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1 p-2 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Tutorial iPhone
              </a>
              <a 
                href="https://youtu.be/gG73B_UHJ54" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1 p-2 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Tutorial Android
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StazioneDigitaleCard;
